import { useEffect, useRef, useState } from "react";
import { getTextRecommendations } from "../../../../api/api";
import { useUser } from "../../../../context/UserContext";

// Copy-examples button is a prototyping aid (see copyExamples below), not
// something end users need — hidden for now rather than deleted, since the
// questionnaire-design experiment it supports is still in progress.
const SHOW_COPY_EXAMPLES = false;

// Option A from the design mockups (dismissible banner → expandable match
// list) — see /home/jns/.claude/plans/serialized-crafting-blum.md. Separate
// feature from the LLM-prompt example system (PromptExamples.jsx) even
// though it reuses the same section/block addressing convention — settings
// live on their own admin page (Tekstisuositukset.jsx), not on /llm-promptit.
//
// Matches can carry independent flags: `primary` (the report pinned in
// this report's Kansilehti as the text-recommendation template — always
// first, bypasses year range/ready-only), `participated` (the current
// user is on that report's team, report_users — same check as the Raportit
// tab's "Vain omat" filter), and `sameRakennusluokitus` (the candidate's
// main building shares the current report's building classification —
// shown regardless of whether the strict match_rakennusluokitus setting is
// on). Any combination can be true at once. A primary match can also carry
// `noMatch: true` — a template report IS pinned, it just has no text for
// THIS exact block — shown as a quiet note instead of silently vanishing,
// so a configured-but-empty primary doesn't look identical to no primary.
//
// Each match also carries id_kiinteisto/reportType/sectionType/sectionId —
// everything needed to build the report editor link, same URL shape as
// AllReports.jsx's getReportUrl (lands on the exact section the match's
// text came from, not just the report's first section).
function matchReportUrl(m) {
  if (!m.sectionId) return null;
  const base = m.id_kiinteisto
    ? `/taloyhtiokortti/${m.id_kiinteisto}/raportti/${m.reportType}/${m.report_id}`
    : `/taloyhtiokortti/raportti/${m.reportType}/${m.report_id}`;
  return `${base}/${m.sectionType}/${m.sectionId}`;
}

// Same caption-flag pattern as CoverPageForm.jsx's PickerRow (FlagCaption) —
// kept as a separate copy here since this row also carries `primary`, which
// the Kansilehti picker's rows never do. Same state->label mapping as
// AllReports.jsx's renderStatusBadge: ready stays unmarked (the expected
// case), archived/anything else gets its own caption. Quieter neutral-gray
// palette than the original amber/orange — only the status flag (draft/
// archived) keeps a warm color, since that one's worth noticing.
function FlagCaption({ primary, participated, sameRakennusluokitus, state }) {
  const statusFlag = state === "ready" ? null
    : state === "archived" ? { text: "Arkistoitu", color: "#8e8e93" }
    : state ? { text: "Luonnos", color: "#b45309" } : null;
  const flags = [
    primary && { text: "Malliraportti", color: "#6e6e73" },
    participated && { text: "Olit mukana", color: "#6e6e73" },
    sameRakennusluokitus && { text: "Sama rakennustyyppi", color: "#6e6e73" },
    statusFlag,
  ].filter(Boolean);
  if (!flags.length) return null;
  return (
    <div style={{ fontSize: "0.68rem", fontWeight: 650, letterSpacing: "0.03em", textTransform: "uppercase", margin: "2px 0 1px" }}>
      {flags.map((f, i) => (
        <span key={f.text}>
          {i > 0 && <span style={{ color: "#d1d1d6" }}> · </span>}
          <span style={{ color: f.color }}>{f.text}</span>
        </span>
      ))}
    </div>
  );
}

// Structural layout (flex direction/widths) lives in these classes, not
// inline styles, so the media query below can reflow rows on narrow
// screens — inline styles can't be overridden by a media query.
function MatchRow({ m, last, onUse }) {
  const url = matchReportUrl(m);
  const name = m.kohteen_nimi || m.osoite || `Raportti ${m.report_id}`;
  return (
    // trs-row-in: every MatchRow is a freshly-mounted element whenever it
    // appears (the list is keyed by report_id, and a manual refresh always
    // swaps in previously-unseen report ids) — so a plain CSS mount
    // animation, with no extra state to track, correctly plays exactly when
    // a row is genuinely new: once on first load, and again for each new
    // batch after a refresh.
    <div className="trs-row trs-row-in" style={{ padding: "10px 14px", borderBottom: last ? "none" : "1px solid #ededf0" }}>
      <div className="trs-meta" style={{ fontSize: "0.72rem", color: "#6e6e73", lineHeight: 1.5 }}>
        <div style={{ fontWeight: 600 }}>
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" title="Avaa raportti uuteen välilehteen"
              style={{ color: "#1d1d1f", textDecoration: "underline", textDecorationColor: "#d1d1d6" }}>
              {name}
            </a>
          ) : (
            <span style={{ color: "#1d1d1f" }}>{name}</span>
          )}
        </div>
        <FlagCaption primary={m.primary} participated={m.participated} sameRakennusluokitus={m.sameRakennusluokitus} state={m.state} />
        {m.osoite && <div style={{ color: "#a1a1a6" }}>{m.osoite}</div>}
        {!m.noMatch && (m.diff != null || m.distanceKm != null) && (
          <div style={{ marginTop: 1, fontVariantNumeric: "tabular-nums" }}>
            {m.diff != null && <span style={{ fontWeight: 650, color: m.diff === 0 ? "#1d7a46" : "#48484a" }}>±{m.diff} v</span>}
            {m.diff != null && m.distanceKm != null && " · "}
            {m.distanceKm != null && <span style={{ color: "#a1a1a6" }}>{String(m.distanceKm).replace(".", ",")} km</span>}
          </div>
        )}
      </div>
      {m.noMatch ? (
        <div style={{ flex: 1, fontSize: "0.8rem", color: "#8e8e93", fontStyle: "italic" }}>
          Ei vastaavaa kohtaa tälle kentälle malliraportissa.
        </div>
      ) : (
        <>
          <div style={{ flex: 1, fontSize: "0.84rem", color: "#1d1d1f", lineHeight: 1.5 }}>{m.text}</div>
          <div className="trs-actions">
            <button type="button" className="trs-use" onClick={() => onUse?.(m.text)}>
              Käytä
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Chevron that rotates 90° when the group it belongs to is expanded.
function Chevron({ expanded }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#c7c7cc" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform .15s", flexShrink: 0 }}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

const ghostBtnStyle = { border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#8e8e93", padding: 5, borderRadius: 6, flexShrink: 0 };

export default function TextRecommendationBanner({ reportId, sectionType, blockId, blockLabel, onUse, onInsert }) {
  // Same admin gate as the Kansilehti "Asetukset ↗" link (CoverPageForm.jsx)
  // — settings is only reachable from there today, which means leaving the
  // Kansilehti tab to go answer a question in a textarea strands you with
  // no way back to settings without re-navigating. Mirroring the link here.
  const { meUser, localUser } = useUser();
  const isTextRecommendationsAdmin = (meUser || localUser)?.rooli === "admin";
  const [matches, setMatches] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  // Shown briefly after a manual refresh completes — the match ranking is
  // deterministic (same report/section/building-type inputs), so refreshing
  // often returns byte-identical data with nothing to visibly change, and a
  // warm backend can respond in well under 100ms. Without an explicit
  // confirmation, a refresh that "worked" is indistinguishable from the
  // button silently doing nothing.
  const [justRefreshed, setJustRefreshed] = useState(false);
  const justRefreshedTimeoutRef = useRef(null);
  // Same transient-confirmation pattern as justRefreshed, but for the
  // separate "copy examples as JSON" action below — kept as its own state
  // rather than reusing justRefreshed since they're two different buttons
  // confirming two different actions.
  const [justCopied, setJustCopied] = useState(false);
  const justCopiedTimeoutRef = useRef(null);
  // Every fetch (the mount effect, and the manual refresh button) claims a
  // request id — a response only gets applied if it's still the latest one,
  // so a manual refresh correctly supersedes an in-flight effect fetch (or
  // vice versa) instead of a stale response clobbering a newer result.
  const requestIdRef = useRef(0);
  // report_ids of "rest" matches already shown this mount — sent as
  // exclude_report_ids on manual refreshes so the backend's already-ranked
  // (but previously untruncated) candidate pool surfaces the next-best
  // not-yet-seen matches instead of repeating the same deterministic top-N.
  // A ref, not state: the exhaustion-wraparound retry below needs a
  // synchronous read/write that a same-tick setState can't give it.
  const seenRestIdsRef = useRef([]);

  const fetchMatches = (isManual = false) => {
    if (!reportId || !sectionType) return;
    const requestId = ++requestIdRef.current;
    const startedAt = Date.now();
    setLoading(true);
    const excludeReportIds = isManual ? seenRestIdsRef.current : [];
    getTextRecommendations({ reportId, sectionType, blockId, blockLabel, excludeReportIds })
      .then(r => {
        if (requestIdRef.current !== requestId) return;
        const data = r.data || [];
        const newRestIds = data.filter(m => !m.primary).map(m => m.report_id);
        if (isManual && newRestIds.length === 0 && seenRestIdsRef.current.length > 0) {
          // Every eligible candidate has been shown at least once — wrap
          // back around to the top-ranked set instead of leaving the user
          // looking at an empty list after enough refreshes.
          seenRestIdsRef.current = [];
          fetchMatches(true);
          return;
        }
        setMatches(data);
        // Record what's now showing regardless of isManual — the initial
        // (non-manual) mount fetch's matches need to be excluded too, or
        // the FIRST refresh click always has an empty exclude list and gets
        // the same deterministic top set back as a no-op; only the second
        // click onward would actually change anything.
        seenRestIdsRef.current = [...new Set([...seenRestIdsRef.current, ...newRestIds])];
        if (isManual) {
          clearTimeout(justRefreshedTimeoutRef.current);
          setJustRefreshed(true);
          justRefreshedTimeoutRef.current = setTimeout(() => setJustRefreshed(false), 1800);
        }
      })
      .catch(() => { if (requestIdRef.current === requestId) setMatches([]); })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        // Keep the spin visible for at least 400ms so a near-instant
        // response doesn't read as "the button did nothing."
        const remaining = Math.max(0, 400 - (Date.now() - startedAt));
        setTimeout(() => { if (requestIdRef.current === requestId) setLoading(false); }, remaining);
      });
  };

  useEffect(() => {
    seenRestIdsRef.current = [];
    fetchMatches(false);
    return () => {
      clearTimeout(justRefreshedTimeoutRef.current);
      clearTimeout(justCopiedTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, sectionType, blockId, blockLabel]);

  if (matches.length === 0) return null;

  const primaryMatch = matches.find(m => m.primary);
  const rest = matches.filter(m => !m.primary);
  const hasRest = rest.length > 0;
  // Pinned template report selected, but it has no text for this exact
  // block, and nothing else matched either — nothing left to show but the
  // quiet note itself (no group card underneath it).
  const primaryEmptyOnly = primaryMatch?.noMatch && !hasRest;

  // Dumps whatever's currently loaded (no new fetch) as numbered, blank-line
  // -separated plain text — for manually pasting into Claude/ChatGPT while
  // prototyping a future "build a multiple-choice questionnaire from N
  // example texts" feature. A JSON array (comma + single newline between
  // entries, no blank line) read as one dense blob once the texts were
  // paragraph-length — this is just as easy for an LLM to read and far
  // easier for a human to visually scan. Only the texts themselves, no
  // report metadata. The primary match counts as a real example too; the
  // max count is whatever the admin's max_suggestions setting already caps
  // it at.
  const copyExamples = () => {
    const texts = matches.filter(m => !m.noMatch).map(m => m.text);
    const payload = texts.map((t, i) => `${i + 1})\n${t}`).join("\n\n");
    if (!navigator.clipboard) {
      console.warn("Leikepöytä ei ole käytettävissä (vaatii suojatun yhteyden/HTTPS).");
      return;
    }
    navigator.clipboard.writeText(payload)
      .then(() => {
        clearTimeout(justCopiedTimeoutRef.current);
        setJustCopied(true);
        justCopiedTimeoutRef.current = setTimeout(() => setJustCopied(false), 1800);
      })
      .catch(err => console.warn("Kuvatekstien kopiointi leikepöydälle epäonnistui", err));
  };

  const refreshBtn = (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      {justRefreshed && !loading && (
        <span style={{ fontSize: "0.68rem", fontWeight: 600, color: "#15803d", display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
          Päivitetty
        </span>
      )}
      <button type="button" onClick={() => fetchMatches(true)} disabled={loading} title="Hae uudet samankaltaiset" aria-label="Päivitä"
        style={{ ...ghostBtnStyle, opacity: loading ? 0.5 : 1, cursor: loading ? "default" : "pointer" }}>
        <i className={`bi bi-arrow-clockwise${loading ? " trs-spin" : ""}`} style={{ fontSize: "0.8rem" }}></i>
      </button>
    </div>
  );

  return (
    <div style={{ padding: "6px 2px 10px" }}>
      <style>{`
        .trs-row { display: flex; gap: 12px; align-items: flex-start; }
        .trs-meta { flex-shrink: 0; width: 170px; }
        .trs-actions { flex-shrink: 0; display: flex; flex-direction: column; gap: 4px; align-self: center; }
        .trs-use { border: none; background: #e3f3ea; color: #15803d; border-radius: 7px; padding: 6px 13px; font-size: 0.74rem; font-weight: 650; white-space: nowrap; cursor: pointer; }
        .trs-use:hover { background: #cdeadb; }
        .trs-spin { display: inline-block; animation: trs-spin-kf 0.7s linear infinite; }
        @keyframes trs-spin-kf { to { transform: rotate(360deg); } }
        @keyframes trs-row-in-kf {
          from { opacity: 0; transform: translateY(-5px); background-color: #eaf7ef; }
          60%  { background-color: #eaf7ef; }
          to   { opacity: 1; transform: translateY(0); background-color: transparent; }
        }
        .trs-row-in { animation: trs-row-in-kf 0.6s ease; }
        .trs-ghost:hover { background: #f0f0f2; }
        .trs-grouptoggle:hover { background: #f0f0f2; }
        @media (max-width: 480px) {
          .trs-row { flex-direction: column; }
          .trs-meta { width: 100%; }
          .trs-actions { flex-direction: row; width: 100%; align-self: auto; }
          .trs-actions button { flex: 1; }
        }
      `}</style>

      {/* Settings (admin-only, same gate as Kansilehti's link) + copy
          (hidden for now, see SHOW_COPY_EXAMPLES). */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 5, padding: "0 2px 4px" }}>
        {isTextRecommendationsAdmin && (
          <a href="/tekstisuositukset" target="_blank" rel="noopener noreferrer"
            style={{ fontSize: "0.68rem", fontWeight: 600, color: "#4338ca", textDecoration: "none", marginRight: 2, whiteSpace: "nowrap" }}>
            Asetukset ↗
          </a>
        )}
        {SHOW_COPY_EXAMPLES && justCopied && (
          <span style={{ fontSize: "0.68rem", fontWeight: 600, color: "#15803d", display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
            Kopioitu
          </span>
        )}
        {SHOW_COPY_EXAMPLES && (
          <button type="button" className="trs-ghost" style={ghostBtnStyle} onClick={copyExamples} title="Kopioi esimerkit" aria-label="Kopioi esimerkit">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" /></svg>
          </button>
        )}
      </div>

      {/* Pinned primary match — a quiet callout (left accent bar), not a
          colored background block, so it reads as "featured content" rather
          than a warning/alert tint. */}
      {primaryMatch && (
        primaryMatch.noMatch ? (
          <div style={{ padding: "2px 4px 4px", fontSize: "0.8rem", color: "#8e8e93", fontStyle: "italic" }}>
            Malliraportti on valittu, mutta sillä ei ole tätä kenttää.
          </div>
        ) : (
          <div style={{ borderLeft: "3px solid #198754", borderRadius: "0 8px 8px 0", background: "#fafbfa" }}>
            <MatchRow m={primaryMatch} last onUse={onUse} />
          </div>
        )
      )}

      {/* The ranked "rest" matches live in their own settings-style
          disclosure group — refresh is part of THIS group's own header, so
          it only ever appears once this group is open, never pre-emptively
          next to a collapsed list. */}
      {!primaryEmptyOnly && (
        <div style={{ border: "1px solid #e5e5ea", borderRadius: 10, background: "#fff", overflow: "hidden", marginTop: primaryMatch ? 10 : 0 }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            {hasRest ? (
              <button type="button" className="trs-grouptoggle" onClick={() => setExpanded(v => !v)}
                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, border: "none", background: "none", cursor: "pointer", padding: "9px 11px", font: "inherit", borderRadius: 10 }}>
                <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#1d1d1f" }}>
                  {primaryMatch ? "Muut samankaltaiset" : "Samankaltaiset kuvaukset"}
                </span>
                <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#8e8e93", background: "#f0f0f2", borderRadius: 10, padding: "1px 8px" }}>
                  {rest.length}
                </span>
                <Chevron expanded={expanded} />
              </button>
            ) : (
              <span style={{ flex: 1, textAlign: "center", padding: "9px 11px", fontSize: "0.82rem", fontWeight: 600, color: "#a1a1a6" }}>
                Ei muita samankaltaisia vielä
              </span>
            )}
            {(expanded || !hasRest) && <div style={{ paddingRight: 9 }}>{refreshBtn}</div>}
          </div>
          {expanded && hasRest && (
            <>
              <div style={{ height: 1, background: "#e5e5ea" }} />
              {rest.map((m, i) => (
                <MatchRow key={m.report_id ?? i} m={m} last={i === rest.length - 1} onUse={onUse} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
